import { Button, Checkbox, Icon, Intent } from "@blueprintjs/core";
import { debounce, isEmpty } from "lodash";
import { useEffect, useRef, useState } from "react";
import SingleFormInput from "components/SingleFormInput";
import { validate } from "utils/form";
import {
  selectUpdateUser,
  selectVerifyCode,
  selectVerifyCodeError,
  selectVerifyCodeBusy,
  verifyCode,
  updateUser,
  resetUser,
} from "controllers/user/action";
import { useDispatch, useSelector } from "react-redux";
import { selectUpdateUserError } from "controllers/user/action";
import { setCurrentDialogue, setMessage } from "controllers/common/action";
import { selectVerifyCodeRequest } from "controllers/user/action";
import { Analytics } from "utils/analytics";

interface VerifyCodeProps {
  email: string;
}

export default function VerifyCode(props: VerifyCodeProps) {
  const { email } = props;

  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [errorPassword, setErrorPassword] = useState("");

  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);

  const [showPW, setShowPW] = useState(false);

  const timer = 60;
  const [counting, setCounting] = useState(true);
  const [timeLeft, setTimeLeft] = useState(timer);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const dispatch = useDispatch();
  const verifyRequest = useSelector(selectVerifyCodeRequest);
  const verifySuccess = useSelector(selectVerifyCode);
  const verifyBusy = useSelector(selectVerifyCodeBusy);
  const verifyError = useSelector(selectVerifyCodeError);

  useEffect(() => {
    Analytics.getInstance().event("Verify Code", "Opened");
    return () => {};
  }, []);

  useEffect(() => {
    if (verifySuccess) {
      setVerified(true);
      setVerifying(false);
    } else if (verifyBusy) {
      setVerified(false);
      setVerifying(true);
    } else if (!isEmpty(verifyError)) {
      setVerified(false);
      setVerifying(false);
    } else {
      setVerified(false);
      setVerifying(false);
    }
  }, [verifyBusy, verifySuccess, verifyError]);

  const [preCode, setPreCode] = useState("");
  useEffect(() => {
    if (!verifying) {
      if (code.length >= 8 && code !== preCode) {
        setPreCode(code);
        const payload = { email: email, verifyCode: code };
        dispatch(verifyCode(payload));
      }
    }
  }, [verifying, code, dispatch, preCode, email]);

  const resetPWSuccess = useSelector(selectUpdateUser);
  const resetPWError = useSelector(selectUpdateUserError);

  useEffect(() => {
    if (!isEmpty(resetPWSuccess)) {
      setErrorPassword("");
      dispatch(setMessage({text: "Your password has been reset", intent: Intent.SUCCESS}));
      dispatch(setCurrentDialogue(""));
    }
  }, [resetPWSuccess, dispatch]);

  useEffect(() => {
    setCounting(true);
    return () => {
      setCode("");
      setPassword("");
      setVerified(false);
      setVerifying(false);
    };
  }, []);

  useEffect(() => {
    if (counting) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((t) => t - 1);
      }, 1000);
      return () => clearInterval(intervalRef.current as NodeJS.Timeout);
    }
  }, [counting]);

  useEffect(() => {
    if (timeLeft <= 0) {
      setCounting(false);
      clearInterval(intervalRef.current as NodeJS.Timeout);
    }
  }, [timeLeft]);

  const debounceField = debounce(({ value }) => {
    const { isValid, getMessage } = validate("text");
    if (!isValid(value, [])) {
      setErrorPassword(getMessage(value, []) as string);
    } else {
      setErrorPassword("");
    }
  }, Number(process.env.REACT_APP_DEBOUNCE));

  const handleVerify = (event: any) => {
    event.preventDefault();
    setCode(event.target.value);
  };

  const handleChangePassword = (event: any) => {
    event.preventDefault();
    const { value } = event.target;
    setPassword(value);
    debounceField({ value });
  };
  const handleSubmit = (event: any) => {
    event.preventDefault();
    if (verified) {
      if (!errorPassword && password) {
        const payload = { email: email, newPassword: password };
        dispatch(updateUser(payload));
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <p>
        We sent your email <strong>{email}</strong> a reset verification code, please continue resetting your password
        by entering the code you received.
      </p>
      <div>
        <div className="form--oneline">
          <SingleFormInput
            name="code"
            type="text"
            label="Verification Code"
            value={code}
            maxLength={12}
            intent={verifyError ? Intent.DANGER : Intent.NONE}
            handleChange={handleVerify}
          />
          {(verified || verifying) && (
            <div className="verify">
              <Icon {...(verified ? { icon: "tick", intent: "success" } : { icon: "circle", intent: "primary" })} />
              {verified ? (
                <span className="verified">Correct verification code</span>
              ) : (
                <span className="verifying">Verifying</span>
              )}
            </div>
          )}

          {!(verified || verifying) && (
            <div className="button-right" hidden={verified || verifying}>
              <Button
                icon="send-message"
                outlined
                intent="primary"
                text="Resend Code"
                disabled={timeLeft > 0}
                onClick={() => {
                  setCounting(true);
                  setTimeLeft(timer);
                  dispatch(resetUser({ email: email }));
                }}
              />
              <p hidden={timeLeft <= 0}>({timeLeft} seconds)</p>
            </div>
          )}
        </div>
        {verifyError && verifyRequest?.verifyCode === code ? (
          <span className="red helperText">{verifyError.error}</span>
        ) : (
          ""
        )}
      </div>

      <SingleFormInput
        name="newPassword"
        type={showPW ? "text" : "password"}
        label="New Password"
        error={resetPWError ? resetPWError.error : errorPassword ? errorPassword : ""}
        value={password}
        handleChange={handleChangePassword}
      />
      <Checkbox checked={showPW} onChange={() => setShowPW(!showPW)}>
        {"Show Password"}
      </Checkbox>
      <div className="form__footer">
        <div></div>
        <Button
          className="button-submit"
          intent="primary"
          type="submit"
          disabled={!verified || !isEmpty(errorPassword)}
          icon="reset"
          text="Reset Password"
        />
      </div>
    </form>
  );
}
